import React, { useEffect, useMemo, useState } from "react"
import { Button, Card } from "@blueprintjs/core"
import { useNavigate, useParams } from "react-router-dom"
import { useLazyQuery } from "@apollo/client"
import CustomButton from "components/Atoms/CustomButton"
import StringInput from "components/Atoms/StringInput"
import { PACKAGING_MATERIAL_DETAIL } from "graphql/scheduling/scheduling.queries.gql"
import ButtonMutation from "components/Molecules/ButtonMutation"
import { ADD_UPDATE_PACKAGING_MATERIAL } from "graphql/scheduling/scheduling.mutations.gql"
import AppToaster from "utils/AppToaster"
import { graphqlError } from "utils/parseError"
import FormController from "components/Molecules/FormController"
import { cloneDeep, set } from "lodash-es"
import CustomSpinner from "components/Atoms/CustomSpinner"
import { AppPermissions } from "constants/permissions"
import useAuthorization from "hooks/useAuthorization"
const HAND_WISE_KEYS = [
  { label: "4 HAND", key: "fourHand" },
  { label: "5 HAND", key: "fiveHand" },
  { label: "6 HAND", key: "sixHand" },
  { label: "7/8 HAND", key: "sevenEightHand" },
  { label: "CL", key: "cluster" },
]

function PackagingMaterialDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [packagingMaterialDetail, setPackagingMaterialDetail] = useState(null)
  const [receivedInputs, setReceivedInputs] = useState({})
  const [validationErrorMessage, setValidationErrorMessage] = useState("")
  const [receivedTareInputs, setReceivedTareInputs] = useState({})
  const [refetchLoading, setRefetchLoading] = useState(false)
  const [isUnlocked, setIsUnlocked] = useState(false)

  const hasRecalculatePermission = useAuthorization([
    AppPermissions.canRecalculatePackagingMaterial,
  ])

  const [getPackagingMaterialDetail, { loading, refetch }] = useLazyQuery(
    PACKAGING_MATERIAL_DETAIL,
    {
      onCompleted: data => {
        if (data?.packagingMaterial)
          setPackagingMaterialDetail(data.packagingMaterial)
        const initialReceived = {}
        const initialTare = {}
        data?.packagingMaterial?.packagingMaterialItems.forEach(item => {
          if (
            item.receivedQuantity === null ||
            item.receivedQuantity === undefined
          ) {
            initialReceived[item.id] = ""
            initialTare[item.id] = item.recievedTareWt || ""
          }
        })
        setReceivedInputs(initialReceived)
        setReceivedTareInputs(initialTare)
      },
      onError: err => console.error("GraphQL Error:", err),
    }
  )

  useEffect(() => {
    if (id && parseInt(id)) getPackagingMaterialDetail({ variables: { id } })
  }, [id])

  const detail = packagingMaterialDetail

  const packagingMaterialItems = useMemo(() => {
    return (
      detail?.packagingMaterialItems?.map(item => ({
        id: item.id || "",
        sku: item.skuType?.name || "-",
        selfWeight: item.skuType?.selfWeight || 0,
        capacity: item.skuType?.capacity || 0,
        harvestedBoxCount: item.harvestedBoxCount || 0,
        receivedQuantity: item.receivedQuantity,
        mismatchBoxCount: item.mismatchBoxCount || 0,
        recievedTareWt: item.recievedTareWt || 0,
        harvestedGrossWt: item.harvestedGrossWt || 0,
        harvestedNetWt: item.harvestedNetWt || 0,
        remark: item.remark || "",
        dispatchedBoxCount: item.dispatchedBoxCount || 0,
      })) || []
    )
  }, [detail])

  const recalculatedItems = useMemo(() => {
    return packagingMaterialItems.map(item => {
      const harvestedBox = item.harvestedBoxCount || 0
      const selfWeight = item.selfWeight || 0
      const capacity = item.capacity || 0

      const receivedQty =
        receivedInputs[item.id] !== undefined
          ? parseInt(receivedInputs[item.id]) || 0
          : item.receivedQuantity || 0

      const mismatch = receivedQty === 0 ? 0 : harvestedBox - receivedQty

      const harvestedGrossWt = harvestedBox * capacity - mismatch * selfWeight

      const tare =
        receivedTareInputs[item.id] !== undefined || ""
          ? parseFloat(receivedTareInputs[item.id]) || 0
          : receivedInputs[item.id] !== undefined
          ? receivedQty * selfWeight
          : item.recievedTareWt || 0

      const net = harvestedGrossWt - tare

      return {
        ...item,
        receivedQty,
        mismatch,
        harvestedBox,
        harvestedGrossWt,
        tare,
        net,
      }
    })
  }, [packagingMaterialItems, receivedInputs, receivedTareInputs])

  const hasMissingReceivedQuantity = useMemo(() => {
    return packagingMaterialItems.some(
      item =>
        item.receivedQuantity === null || item.receivedQuantity === undefined
    )
  }, [packagingMaterialItems])

  const harvestBoxCounts = useMemo(() => {
    return (
      detail?.combinedPackagingMaterialItems?.map(item => ({
        sku: item.skuType?.name || "-",
        selfWeight: item.skuType?.selfWeight || 0,
        dispatchedCount: item.dispatchedBoxCount || 0,
        dispatchedWeight: item.dispatchedBoxWt || 0,
        harvestCount: item.harvestedBoxCount || 0,
        harvestWeight: item.harvestedBoxWt || 0,
        ...HAND_WISE_KEYS.reduce((acc, h) => {
          acc[h.key] = item[h.key] || 0
          return acc
        }, {}),
      })) || []
    )
  }, [detail])

  const handWiseTotals = useMemo(() => {
    return harvestBoxCounts.reduce(
      (acc, item) => {
        let rowSum = 0
        HAND_WISE_KEYS.forEach(h => {
          acc[h.key] += item[h.key]
          rowSum += item[h.key]
        })
        acc.total += rowSum
        return acc
      },
      HAND_WISE_KEYS.reduce((acc, h) => ({ ...acc, [h.key]: 0 }), { total: 0 })
    )
  }, [harvestBoxCounts])

  const boxTotals = useMemo(() => {
    return harvestBoxCounts.reduce(
      (acc, item) => ({
        dispatchedCount: acc.dispatchedCount + item.dispatchedCount,
        dispatchedWeight: acc.dispatchedWeight + item.dispatchedWeight,
        harvestCount: acc.harvestCount + item.harvestCount,
        harvestWeight: acc.harvestWeight + item.harvestWeight,
      }),
      {
        dispatchedCount: 0,
        dispatchedWeight: 0,
        harvestCount: 0,
        harvestWeight: 0,
      }
    )
  }, [harvestBoxCounts])

  const receivedBoxTotals = useMemo(() => {
    return recalculatedItems.reduce(
      (acc, item) => {
        acc.harvestedBoxCount += item.harvestedBox
        acc.receivedQuantity += item.receivedQty
        acc.mismatchBoxCount += item.mismatch
        acc.recievedTareWt += item.tare
        acc.harvestedGrossWt += item.harvestedGrossWt
        acc.harvestedNetWt += item.net
        return acc
      },
      {
        harvestedBoxCount: 0,
        receivedQuantity: 0,
        mismatchBoxCount: 0,
        recievedTareWt: 0,
        harvestedGrossWt: 0,
        harvestedNetWt: 0,
      }
    )
  }, [recalculatedItems])

  const weights = {
    leaf: detail?.leafWt || 0,
    finalWeight: detail?.finalWt || 0,
    slip: detail?.weightSlip || 0,
    wastage: detail?.wasteageWt || 0,
    totalHarvestWeight: detail?.totalHarvestWt || 0,
    totalHarvestedBoxWeight: detail?.totalHarvestedBoxWt || 0,
    totalDispatchedBoxWeight: detail?.totalDispatchedBoxWt || 0,
    fruitWeight: detail?.fruitWt || 0,
    totalReceivedTareWeight: detail?.totalReceivedTareWt || 0,
    fruitAmt: detail?.fruitAmt || 0,
    wastageAmt: detail?.wastageAmt || 0,
    stickAmt: detail?.stickAmt || 0,
    deductionAmt: detail?.deductionAmt || 0,
    totalAmt: detail?.totalAmt || 0,
    fruitAndWastageWeight: detail?.fruitAndWastageWt || 0,
    stickWeightPercent: detail?.stickWtPercent || 0,
    stickWeight: detail?.stickWt || 0,
    deductionWeightPercent: detail?.deductionWtPercent || 0,
    deductionWeight: detail?.deductionWt || 0,
    netWeightAfterDeduction: detail?.netWeightAfterDeduction || 0,
    remark: detail?.remark || "",
    attachments: detail?.attachments || [],
    approvedRate: detail?.schedule?.gradePrices?.[0]?.price || 0,
    fruitNetWeight: detail?.fruitNetWt || 0,
    avgRate: detail?.avgRate || 0,
    finalAmt: detail?.finalAmt || 0,
  }

  if (loading || !detail || refetchLoading)
    return (
      <div className="p-4 flex justify-center items-center h-40">
        <CustomSpinner size={50} />
      </div>
    )
  const harvestDetails = {
    Date: new Date(detail.createdAt).toLocaleString(),
    "Gatepass No": detail.schedule?.gatePass || "-",
    "Vehicle No": detail.schedule?.vehicle?.registrationNumber || "-",
    "Field Supervisor": detail.fieldSupervisorName?.fullName || "-",
    "P.M Supervisor": detail.pmSupervisorName?.fullName || "-",
    "Area Incharge": detail.areaInchargeName?.fullName || "-",
    "Total Boxes": detail.totalQuantity || 0,
    "Plot Visitor": detail.plotVisitorName?.fullName || "-",
    "User Type": detail.supplierType || "-",
    // "Farmer Name": detail.referenceSupplier || "-",
  }

  return (
    <Card className="m-3 p-0">
      <div className="p-3 flex items-center border-b border-gray-300">
        <CustomButton onClick={() => navigate(-1)} minimal icon="arrow-left" />
        <h6 className="ml-3 my-0">Packaging Material</h6>
      </div>

      <div className="p-4 space-y-3 px-64">
        <div className="border-legacy rounded bg-white">
          <p className="bg-gray-100 p-3 text-lg">Harvested Info.</p>
          <div className="underline px-3">Basic Details</div>
          <div className="grid grid-cols-4 gap-4 text-sm mt-2 p-3">
            {Object.entries(harvestDetails).map(([label, value]) => (
              <div key={label}>
                <strong>{label.replace(/([A-Z])/g, " $1")}: </strong>
                <br /> {value}
              </div>
            ))}
          </div>

          <div className="border-legacy rounded m-3 bg-white">
            <p className="p-2 text-base underline">
              Harvest Box Counts Details
            </p>
            <div className="grid grid-cols-5 gap-4 bg-gray-100 p-2 text-sm font-500">
              <div>SKU TYPE</div>
              <div>DISPATCHED BOX COUNT</div>
              <div>DISPATCHED BOX WEIGHT</div>
              <div>HARVESTED BOX COUNT</div>
              <div>HARVESTED BOX WEIGHT</div>
            </div>
            {harvestBoxCounts.map((item, i) => (
              <div
                key={i}
                className="grid grid-cols-5 gap-4 border-t border-gray-200 px-2 py-3 text-sm"
              >
                <div>
                  {item.sku}
                  <br />
                  <span className="text-xs">Tare Wt. in /kg</span>{" "}
                  <span className="text-brown-400 text-xs">
                    {item.selfWeight}
                  </span>
                </div>
                <div>{item.dispatchedCount}</div>
                <div className="text-brown-400">{item.dispatchedWeight}</div>
                <div>{item.harvestCount}</div>
                <div className="text-brown-400">{item.harvestWeight}</div>
              </div>
            ))}
            <div className="grid grid-cols-5 gap-4 border-t border-gray-200 px-2 py-3 text-sm bg-green-50 font-500">
              <div>Total</div>
              <div>{boxTotals.dispatchedCount}</div>
              <div className="text-brown-400">
                {boxTotals.dispatchedWeight.toFixed(2)}
              </div>
              <div>{boxTotals.harvestCount}</div>
              <div className="text-brown-400">
                {boxTotals.harvestWeight.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="border-legacy rounded m-3 bg-white">
            <p className="p-2 text-base underline">
              Box Type Hand Wise Details
            </p>
            <div className="grid grid-cols-7 gap-4 bg-gray-100 p-2 text-sm font-500">
              <div>SKU Type</div>
              {HAND_WISE_KEYS.map(h => (
                <div key={h.key}>{h.label}</div>
              ))}
              <div>TOTAL</div>
            </div>
            {harvestBoxCounts.map((item, i) => {
              const rowTotal = HAND_WISE_KEYS.reduce(
                (sum, h) => sum + item[h.key],
                0
              )
              return (
                <div
                  key={i}
                  className="grid grid-cols-7 gap-4 border-t border-gray-200 px-2 py-3 text-sm"
                >
                  <div>{item.sku}</div>
                  {HAND_WISE_KEYS.map(h => (
                    <div key={h.key}>{item[h.key]}</div>
                  ))}
                  <div className="font-semibold">{rowTotal}</div>
                </div>
              )
            })}
            <div className="grid grid-cols-7 gap-4 border-t border-gray-200 px-2 py-3 text-sm bg-green-50 font-500">
              <div>Total</div>
              {HAND_WISE_KEYS.map(h => (
                <div key={h.key}>{handWiseTotals[h.key]}</div>
              ))}
              <div className="font-semibold">{handWiseTotals.total}</div>
            </div>
          </div>
        </div>

        <div className=" space-y-3">
          <div className="grid grid-cols-5 gap-4 text-sm p-3">
            <div className="bg-blue-100 p-4 rounded shadow shadow-blue-300">
              <strong>Leaf Weight:</strong> {weights.leaf} kg
            </div>
            <div className="bg-green-50 p-4 rounded shadow shadow-green-300">
              <strong>Weight Slip:</strong> {weights.slip} kg
            </div>
            <div className="bg-red-100 p-4 rounded shadow shadow-red-300">
              <strong>Wastage Weight:</strong> {weights.wastage} kg
            </div>
            <div className="bg-brown-100 p-4 rounded shadow shadow-brown-300">
              <strong>Stick Weight % :</strong> {weights.stickWeightPercent}%
            </div>
            <div className="bg-cyan-100 p-4 rounded shadow shadow-cyan-300">
              <strong>Deduction Weight % :</strong>{" "}
              {weights.deductionWeightPercent}%
            </div>
          </div>
        </div>

        <FormController
          source={{ ...receivedInputs, ...receivedTareInputs }}
          onChange={(path, value) => {
            const isTare = path.startsWith("tare_")
            const key = path.replace(/^tare_/, "")

            if (isTare) {
              const updated = cloneDeep(receivedTareInputs)
              updated[key] = value
              setReceivedTareInputs(updated)
            } else {
              const updated = cloneDeep(receivedInputs)
              set(updated, path, value)
              setReceivedInputs(updated)

              const item = packagingMaterialItems.find(i => i.id === key)

              if (item?.selfWeight !== undefined) {
                const tareWeight = (
                  parseInt(value || 0) * item.selfWeight
                ).toFixed(2)
                const updatedTare = cloneDeep(receivedTareInputs)
                updatedTare[key] = tareWeight
                setReceivedTareInputs(updatedTare)
              }
            }
          }}
        >
          <div className="border-legacy rounded bg-white">
            <div className="flex justify-between items-center py-2 px-4">
              <p className="text-lg  bg-gray-100">Enter Received Box</p>

              {hasRecalculatePermission && !hasMissingReceivedQuantity && (
                <CustomButton
                  minimal={true}
                  icon={isUnlocked ? "unlock" : "lock"}
                  intent={isUnlocked ? "success" : "warning"}
                  disabled={detail?.schedule?.booking?.status === "CMP"}
                  onClick={() => setIsUnlocked(prev => !prev)}
                />
              )}
            </div>
            <div className="border-legacy rounded m-3 bg-white">
              <div className="grid grid-cols-7 gap-4 bg-gray-100 p-2 text-sm font-500">
                <div>SKU TYPE</div>
                <div>HARVEST BOX</div>
                <div>RECEIVED BOX</div>
                <div>MISMATCHED BOX</div>
                <div>
                  RECEIVED TARE <br /> WEIGHT
                </div>
                <div>
                  HARVEST GROSS <br /> WEIGHT
                </div>
                <div>
                  HARVEST NET <br /> WEIGHT
                </div>
              </div>

              {recalculatedItems.map((item, i) => {
                return (
                  <div
                    key={i}
                    className="grid grid-cols-7 gap-4 border-t border-gray-200 px-2 py-3 text-sm"
                  >
                    <div>
                      {item.sku}
                      <br />
                      <span className="text-xs">Tare Wt. in /kg</span>{" "}
                      <span className="text-brown-400 text-xs">
                        {item.selfWeight}
                      </span>
                    </div>
                    <div>{item.harvestedBox}</div>
                    <div className="text-brown-400">
                      <StringInput
                        type="number"
                        path={`${item.id}`}
                        min={1}
                        value={
                          receivedInputs[item.id] !== undefined
                            ? receivedInputs[item.id]
                            : item.receivedQuantity || ""
                        }
                        disabled={!hasMissingReceivedQuantity && !isUnlocked}
                        big={false}
                        placeholder="Enter"
                      />
                    </div>
                    <div className="text-brown-400">{item.mismatch}</div>
                    <div className="text-brown-400">
                      <StringInput
                        type="number"
                        step="0.01"
                        min={0}
                        path={`tare_${item.id}`}
                        value={
                          receivedTareInputs[item.id] !== undefined
                            ? receivedTareInputs[item.id]
                            : item.recievedTareWt?.toFixed(2) || ""
                        }
                        disabled={!hasMissingReceivedQuantity && !isUnlocked}
                        big={false}
                        placeholder="kg"
                      />
                    </div>
                    <div className="text-blue-400">
                      {item.harvestedGrossWt.toFixed(2)} kg
                    </div>
                    <div className="text-green-300">
                      {item.net.toFixed(2)} kg
                    </div>
                  </div>
                )
              })}

              <div className="grid grid-cols-7 gap-4 border-t border-gray-200 px-2 py-3 text-sm bg-green-50 font-500">
                <div>Total</div>
                <div>{receivedBoxTotals.harvestedBoxCount}</div>
                <div>{receivedBoxTotals.receivedQuantity}</div>
                <div>{receivedBoxTotals.mismatchBoxCount}</div>
                <div className="text-brown-400">
                  {receivedBoxTotals.recievedTareWt.toFixed(2)} kg
                </div>
                <div className="text-blue-400">
                  {receivedBoxTotals.harvestedGrossWt.toFixed(2)} kg
                </div>
                <div className="text-green-300">
                  {receivedBoxTotals.harvestedNetWt.toFixed(2)} kg
                </div>
              </div>
            </div>
          </div>
        </FormController>

        <div className="text-right">
          {(hasMissingReceivedQuantity ||
            (hasRecalculatePermission && isUnlocked)) && (
            <div className="text-right px-4 pb-4 gap-2">
              {/* Optionally, I want to show a warning or info message here */}
              {validationErrorMessage && (
                <div className="bg-yellow-50 mr-2 border-l-4 border-yellow-500 text-yellow-700 p-3 mb-3 rounded text-sm text-left inline-block">
                  ⚠️ {validationErrorMessage}
                </div>
              )}
              <ButtonMutation
                icon="endorsed"
                text="Save"
                mutation={ADD_UPDATE_PACKAGING_MATERIAL}
                variables={{
                  id: id || null,

                  packagingMaterialItems: recalculatedItems.map(item => ({
                    id: item.id,
                    receivedQuantity:
                      receivedInputs[item.id] !== undefined
                        ? parseInt(receivedInputs[item.id])
                        : item.receivedQuantity,
                    receivedTareWt:
                      receivedTareInputs[item.id] !== undefined
                        ? parseFloat(receivedTareInputs[item.id] || 0)
                        : item.recievedTareWt || 0,
                  })),
                }}
                disabled={
                  hasRecalculatePermission
                    ? false
                    : Object.values(receivedInputs).every(
                        qty => qty === "" || qty === null
                      )
                }
                onError={err => {
                  AppToaster.show({
                    message: graphqlError(err.message),
                    intent: "danger",
                    icon: "cross",
                  })
                }}
                validate={() => {
                  for (const item of recalculatedItems) {
                    // In the edit (unlock) case, untouched items won't be in
                    // receivedInputs – fall back to their saved receivedQuantity.
                    const inputQty =
                      receivedInputs[item.id] !== undefined
                        ? receivedInputs[item.id]
                        : item.receivedQuantity

                    if (
                      inputQty === "" ||
                      inputQty === null ||
                      inputQty === undefined ||
                      isNaN(parseInt(inputQty))
                    ) {
                      setValidationErrorMessage(
                        "Please fill received box for all SKUs."
                      )
                      return false
                    }

                    const qty = parseInt(inputQty)

                    if (qty <= 0) {
                      setValidationErrorMessage(
                        `Received Box for SKU "${item.sku}" must be greater than 0.`
                      )
                      return false
                    }

                    if (qty > item.dispatchedBoxCount) {
                      setValidationErrorMessage(
                        `Received Box for SKU "${item.sku}" cannot be more than dispatched box count (${item.dispatchedBoxCount}).`
                      )
                      return false
                    }
                  }

                  setValidationErrorMessage("") // ✅ clear previous errors if any
                  return true
                }}
                onCompleted={async () => {
                  setRefetchLoading(true)

                  AppToaster.show({
                    message: "Packaging Material saved successfully!",
                    intent: "success",
                    icon: "tick",
                  })

                  await refetch({ id })
                    .then(res => {
                      const data = res?.data?.packagingMaterial
                      if (data) {
                        setPackagingMaterialDetail(data)

                        const initialReceived = {}
                        const initialTare = {}
                        data.packagingMaterialItems.forEach(item => {
                          initialReceived[item.id] = item.receivedQuantity ?? ""
                          initialTare[item.id] =
                            item.recievedTareWt?.toFixed(2) ?? ""
                        })
                        setReceivedInputs(initialReceived)
                        setReceivedTareInputs(initialTare)
                      }
                    })
                    .finally(() => {
                      setRefetchLoading(false)
                      setIsUnlocked(false)
                    })
                }}
              />
            </div>
          )}
        </div>

        {!hasMissingReceivedQuantity && (
          <div className="border-legacy rounded bg-white space-y-3">
            <p className="text-lg bg-gray-100 p-3">Weight Calculation</p>

            <div className="rounded p-3 text-sm space-y-2">
              <p className="font-semibold  bg-gray-50 p-1 underline">
                Calculated Values
              </p>
              <ul className="list-disc px-3">
                <li className="flex justify-between">
                  <p className="font-500 text-base">
                    {weights.slip} - {weights.leaf} -{" "}
                    {weights.totalReceivedTareWeight} =
                    <br />
                    <span className="text-xs text-gray-500">
                      Weight slip - Leaf Wt. - Total received box Wt. =
                    </span>
                  </p>

                  <p className="font-500 text-base text-right">
                    {weights.fruitWeight} kg
                    <br />
                    <span className="text-xs text-gray-500">
                      Fruit Wt. (kg)
                    </span>
                  </p>
                </li>
                <li className="flex justify-between">
                  <p className="font-500 text-base">
                    {weights.fruitWeight} + {weights.wastage} =
                    <br />
                    <span className="text-xs text-gray-500">
                      Fruit Wt. + Wastage Wt. =
                    </span>
                  </p>

                  <p className="font-500 text-base text-right">
                    {weights.fruitAndWastageWeight} kg
                    <br />
                    <span className="text-xs text-gray-500">
                      Fruit & Wastage Wt.
                    </span>
                  </p>
                </li>
                <li className="flex justify-between">
                  <p className="font-500 text-base">
                    {weights.fruitAndWastageWeight} *{" "}
                    {weights.stickWeightPercent}
                    % =
                    <br />
                    <span className="text-xs text-gray-500">
                      Fruit & Wastage Wt. * Stick Wt. (
                      {weights.stickWeightPercent}%) =
                    </span>
                  </p>

                  <p className="font-500 text-base text-right">
                    {weights.stickWeight} kg
                    <br />
                    <span className="text-xs text-gray-500">Stick Wt.</span>
                  </p>
                </li>
                <li className="flex justify-between">
                  <p className="font-500 text-base">
                    {weights.fruitAndWastageWeight} + {weights.stickWeight} =
                    <br />
                    <span className="text-xs text-gray-500">
                      Fruit & Wastage Wt. + Sticks Wt. =
                    </span>
                  </p>

                  <p className="font-500 text-base text-right">
                    {weights.totalHarvestWeight} kg
                    <br />
                    <span className="text-xs text-gray-500">Harvest Wt.</span>
                  </p>
                </li>
                <li className="flex justify-between">
                  <p className="font-500 text-base">
                    {weights.totalHarvestWeight} *{" "}
                    {weights.deductionWeightPercent}% =
                    <br />
                    <span className="text-xs text-gray-500">
                      Harvest Wt. * Deduction ({weights.deductionWeightPercent}
                      %) =
                    </span>
                  </p>

                  <p className="font-500 text-base text-right">
                    {weights.deductionWeight} kg
                    <br />
                    <span className="text-xs text-gray-500">
                      Harvest Wt. & Dedu. Percent
                    </span>
                  </p>
                </li>
              </ul>
              <div
                style={{
                  border: "2px solid #3E9920", // Tailwind's green-300
                }}
                className="font-500 bg-green-50 rounded-2xl p-3 text-base font-semibold flex justify-between"
              >
                <span>
                  {weights.totalHarvestWeight} - {weights.deductionWeight}
                  <br />
                  <span className="text-sm text-gray-700">
                    Harvest Wt. - Deduction =
                  </span>
                </span>
                <span className="text-[#3E9920] text-right">
                  {weights.netWeightAfterDeduction} kg
                  <br />
                  <span className="text-sm text-gray-700">
                    Final Harvest Wt.
                  </span>
                </span>
              </div>
              <li className="font-500 p-3 text-base flex justify-between">
                <p className="font-500 text-base">
                  Remark
                  <br />
                  <span className="text-xs text-gray-500">
                    {weights.remark?.split("\n").map((line, idx) => (
                      <React.Fragment key={idx}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </span>
                </p>

                <div className="flex gap-2">
                  {weights.attachments.map(file => {
                    const isPDF = file?.url?.toLowerCase().endsWith(".pdf")

                    return (
                      <a
                        href={file?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={file?.id}
                      >
                        {isPDF ? (
                          <div
                            style={{
                              width: 60,
                              height: 60,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "1px solid #ccc",
                              borderRadius: 4,
                              backgroundColor: "#f9f9f9",
                              fontSize: 12,
                            }}
                          >
                            📄 PDF
                          </div>
                        ) : (
                          <img
                            alt="attachment"
                            src={file?.url}
                            width={80}
                            height={80}
                            style={{ objectFit: "cover", borderRadius: 4 }}
                          />
                        )}
                      </a>
                    )
                  })}
                </div>
              </li>
            </div>
          </div>
        )}

        {!hasMissingReceivedQuantity && (
          <div className="border-legacy rounded bg-white space-y-6 pb-3">
            <p className="text-lg bg-gray-50 p-3">
              Vehicle-wise Unloading Rate Calculation:
            </p>
            <div
              style={{
                border: "1px solid #477FD2",
              }}
              className=" bg-gray-50 rounded p-3 m-3 text-sm"
            >
              <div className="flex items-center gap-2">
                <span className="bg-green-200 text-green-500 px-2 py-1 rounded font-semibold text-sm">
                  Approved Rate
                </span>
                <span className="font-semibold text-base">
                  ₹ {weights.approvedRate} /kg
                </span>
              </div>
              {detail?.supplierType === "VENDOR" && (
                <p className="text-gray-600 mt-2 text-xs">
                  <strong>Note:</strong> If user type selected is Vendor, a
                  fixed charge of ₹2.70 per kg will be added to the base rate.
                  This additional charge is applicable only on the fruit weight.
                  Example: If the base rate is ₹{weights.approvedRate}/kg, the
                  final rate will be: ₹{weights.approvedRate}+ ₹2.70 = ₹
                  {weights.approvedRate + 2.7}/kg.
                </p>
              )}
            </div>

            <div className="rounded text-sm space-y-2 m-3">
              <p className="font-semibold  bg-gray-50 p-1 underline">
                Calculated Values
              </p>
              <ul className="list-disc px-3">
                <li className="flex justify-between">
                  <p className="font-500 text-base">
                    {weights.fruitWeight} * ₹
                    {detail?.supplierType === "VENDOR"
                      ? weights.approvedRate + 2.7
                      : weights.approvedRate}{" "}
                    = <br />
                    <span className="text-xs text-gray-500">
                      Fruit Wt * Rate =
                    </span>
                  </p>

                  <p className="font-500 text-base text-right">
                    + ₹ {weights.fruitAmt}
                    <br />
                    <span className="text-xs text-gray-500">Fruit amt.</span>
                  </p>
                </li>
                <li className="flex justify-between">
                  <p className="font-500 text-base">
                    {weights.wastage} * ₹{weights.approvedRate} =
                    <br />
                    <span className="text-xs text-gray-500">
                      Wastage * Rate =
                    </span>
                  </p>

                  <p className="font-500 text-base text-right">
                    + ₹ {weights.wastageAmt}
                    <br />
                    <span className="text-xs text-gray-500">Wastage amt.</span>
                  </p>
                </li>
                <li className="flex justify-between">
                  <p className="font-500 text-base">
                    {weights.stickWeight} * ₹{weights.approvedRate} =
                    <br />
                    <span className="text-xs text-gray-500">
                      Stick weight ({weights.stickWeightPercent}%) * Rate =
                    </span>
                  </p>

                  <p className="font-500 text-base text-right">
                    + ₹ {weights.stickAmt}
                    <br />
                    <span className="text-xs text-gray-500">Stick amt.</span>
                  </p>
                </li>
                <li className="flex justify-between">
                  <p className="font-500 text-base">
                    {weights.deductionWeight} * ₹{weights.approvedRate} =
                    <br />
                    <span className="text-xs text-gray-500">
                      Deduction ({weights.deductionWeightPercent}%) * Rate =
                    </span>
                  </p>

                  <p className="font-500 text-base text-right">
                    - ₹ {weights.deductionAmt}
                    <br />
                    <span className="text-xs text-gray-500">
                      Deduction Amt.
                    </span>
                  </p>
                </li>
              </ul>
              <div
                style={{
                  border: "2px solid #3E9920", // Tailwind's green-300
                }}
                className="bg-green-50 rounded-2xl p-3 text-base font-500 flex justify-between"
              >
                <span>
                  ₹{weights.fruitAmt} + ₹{weights.wastageAmt} + ₹
                  {weights.stickAmt} - ₹{weights.deductionAmt}
                  <br />
                  <span className="text-sm text-gray-700">
                    Fruit Amt. + Wastage Amt. + Stick Amt. - Deduction Amt
                  </span>
                </span>
                <span className="text-[#3E9920] text-right">
                  ₹ {weights.totalAmt}
                  <br />
                  <span className="text-sm text-gray-700">Total Amt.</span>
                </span>
              </div>
            </div>

            <div className="mt-3 bg-gray-50 p-2 m-3">
              <h3 className="mb-2">Total Calculation Summary</h3>

              <ul className="list-disc px-3">
                <li className="flex justify-between">
                  <p className="font-500 text-base">
                    ₹ {weights.totalAmt} / {weights.finalWeight} kg
                    <br />
                    <span className="text-xs text-gray-500">
                      Total Amount / Final Harvest Wt. =
                    </span>
                  </p>

                  <p className="font-500 text-base text-right">
                    ₹ {weights.avgRate}
                    <br />
                    <span className="text-xs text-gray-500">Rate</span>
                  </p>
                </li>
                <li className="flex justify-between">
                  <p className="font-500 text-base">
                    ₹ {weights.avgRate} * {weights.finalWeight} kg
                    <br />
                    <span className="text-xs text-gray-500">
                      Rate * Final Harvest Wt. =
                    </span>
                  </p>

                  <p className="font-500 text-lg text-[#3E9920] text-right">
                    ₹ {weights.finalAmt}
                    <br />
                    <span className="text-xs text-gray-500">Final Amount</span>
                  </p>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

export default PackagingMaterialDetail
